/**
 * The Briefing — default destination of the Marketer View (SYR-68, Phase C).
 *
 * Sub-tabs: Overview (the Q1–Q6 arc) · Areas. Recommendations is hidden for now
 * (The Action Plan stays a placeholder until Phase D). Driven by the global
 * MarketerScope + the scope-aware `useBriefingData` hook.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useNavReturn } from '../nav/NavReturnContext';
import { Alert, Tabs, Modal } from 'antd';
import { FileTextOutlined, AppstoreOutlined, CheckSquareOutlined, CompassOutlined, RightOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import {
  useGetSystemSettingQuery,
  useGetSurveysQuery,
  useReanalyzeElementMutation,
} from '../../../services/nestjs-base-api';
import { useBriefingData } from '../useBriefingData';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import { OverviewTab } from '../components/OverviewTab';
import { ExploreTab } from '../components/ExploreTab';
import { RecommendationsTab } from '../components/RecommendationsTab';
import { MarketerInsufficientData } from '../components/MarketerInsufficientData';
import { HypothesisCard } from '../components/HypothesisCard';
import { ShareSurveysBanner } from '../components/ShareSurveysBanner';
import { SimpleOnboardingWizardV2 } from '../../SimpleOnboardingWizardV2';
import { THEME } from '../config/theme';
import { Drawer } from '../components';
import { MarketerLoading } from '../atoms/MarketerLoading';
import type { ScoreNode } from '../types';

/** ELEMENT_DESCRIPTIONS system-setting shape: element name → copy variants. */
type ElementDescriptions = Record<string, { mouseover?: string; sidebar?: string }>;

type SubTab = 'overview' | 'areas' | 'recommendations';

export const BriefingPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const {
    data,
    narrative,
    response,
    aiResponse,
    aiLoading,
    narrativeLoading,
    dismissedRecommendationIds,
    refetchNarrative,
    benchmarkMetrics,
    isLoading,
    isError,
    error,
  } = useBriefingData();

  // Recallable "getting started" guide — opens the onboarding step-3 view
  // (roadmap, incentive, share links) read-only in a modal.
  const [guideOpen, setGuideOpen] = useState(false);

  // Sub-tab lives in the URL (?tab=) so "back to The Briefing · Areas" restores it.
  // 'recommendations' is a hidden sub-tab — reachable only via the hero CTA.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const subTab: SubTab =
    tabParam === 'areas'
      ? 'areas'
      : tabParam === 'recommendations'
        ? 'recommendations'
        : 'overview';
  const [drawerNode, setDrawerNode] = useState<ScoreNode | null>(null);

  const briefingLabel = intl.formatMessage({
    id: 'marketerView.nav.briefing',
    defaultMessage: 'The Briefing',
  });
  const sevenName = intl.formatMessage({
    id: 'marketerView.sevenName',
    defaultMessage: 'The 7 Brand Boosters',
  });

  const { brandHash } = useParams<{ brandHash: string }>();
  const { navigateWithReturn } = useNavReturn();
  const marketerRoot = useMarketerRoot();
  const { user } = useAuth();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;

  const [reanalyzeElement] = useReanalyzeElementMutation();

  // The guide is entirely about running the surveys you already have, and its
  // step-3 view renders nothing but a spinner when there are none. Someone who
  // skipped onboarding has no surveys, already sees the "Finish setup" banner,
  // and has nothing to read here — so don't offer the guide at all until at
  // least one active survey exists.
  const { data: surveys = [] } = useGetSurveysQuery(
    { jwtToken, organisationId },
    { skip: !jwtToken || !organisationId },
  );
  const hasActiveSurveys = surveys.some((s: any) => s.status === 'active' && !s.deletedAt);

  // Element descriptions + per-element AI summaries — the SAME sources the v3
  // orbital panel uses, so the Briefing drawer and The Detail show identically.
  const { data: elementDescriptionsData } = useGetSystemSettingQuery(
    { jwtToken, settingName: 'ELEMENT_DESCRIPTIONS' },
    { skip: !jwtToken },
  );
  const elementDescriptions = useMemo<ElementDescriptions>(() => {
    try {
      if (!elementDescriptionsData) return {};
      const parsed =
        typeof elementDescriptionsData === 'string'
          ? JSON.parse(elementDescriptionsData)
          : elementDescriptionsData;
      // Setting shape is { version, elements: { <name>: { sidebar, mouseover } } }.
      return (parsed?.elements ?? parsed) as ElementDescriptions;
    } catch {
      return {};
    }
  }, [elementDescriptionsData]);

  const handleOpen = useCallback((node: ScoreNode): void => setDrawerNode(node), []);
  const handleClose = useCallback((): void => setDrawerNode(null), []);
  const handleViewInViz = useCallback((): void => {
    const element = drawerNode?.canonical;
    setDrawerNode(null);
    if (!brandHash) return;
    const query = element ? `?element=${encodeURIComponent(element)}` : '';
    const tabLabel = subTab === 'areas' ? 'Areas' : 'Overview';
    navigateWithReturn(
      `${marketerRoot}/${brandHash}/detail${query}`,
      `${briefingLabel} · ${tabLabel}`,
    );
  }, [navigateWithReturn, marketerRoot, brandHash, drawerNode, subTab, briefingLabel]);

  // Description + AI summary for the open node, resolved by canonical name.
  const drawerDescription = drawerNode
    ? elementDescriptions[drawerNode.canonical]?.sidebar ??
      elementDescriptions[drawerNode.display]?.sidebar
    : undefined;
  const drawerFirstThoughts = useMemo<string | undefined>(() => {
    if (!drawerNode) return undefined;
    const name = drawerNode.canonical;
    // Prefer the AI pass; fall back to the fast response (usually no summaries).
    const src = aiResponse ?? response;
    if (!src) return undefined;
    const le = src.latentElementScores?.find((l) => l.element === name);
    if (le?.aiSummary) return le.aiSummary;
    const qc = src.questionCategoryScores?.find((q) => q.category === name);
    return qc?.aiSummary;
  }, [drawerNode, aiResponse, response]);

  // Industry-benchmark score for the open element (when this element is in the
  // benchmark set) — shown alongside the brand score in the drawer header.
  const drawerBenchmark = useMemo<
    { industryMean: number; delta: number; level: number } | undefined
  >(() => {
    if (!drawerNode) return undefined;
    const m = benchmarkMetrics.find(
      (b) => b.categoryName === drawerNode.canonical,
    );
    if (!m || typeof m.industryMean !== 'number') return undefined;
    const delta =
      typeof m.vsIndustryMean === 'number'
        ? m.vsIndustryMean
        : typeof m.value === 'number'
          ? m.value - m.industryMean
          : 0;
    return { industryMean: m.industryMean, delta, level: m.level };
  }, [drawerNode, benchmarkMetrics]);

  // Clicking the drawer's benchmark score deep-links to The Benchmarks, scrolled
  // to the relative section (level 1 = latent, else measured); back pill returns.
  const handleBenchmarkClick = useCallback((): void => {
    if (!brandHash || !drawerBenchmark) return;
    const section = drawerBenchmark.level === 1 ? 'latent' : 'measured';
    setDrawerNode(null);
    const tabLabel = subTab === 'areas' ? 'Areas' : 'Overview';
    navigateWithReturn(
      `${marketerRoot}/${brandHash}/benchmarks?section=${section}`,
      `${briefingLabel} · ${tabLabel}`,
    );
  }, [
    brandHash,
    drawerBenchmark,
    subTab,
    navigateWithReturn,
    marketerRoot,
    briefingLabel,
  ]);

  const handleAnalyseAgain = useCallback(async (): Promise<string | undefined> => {
    if (!drawerNode || !response || !jwtToken || !organisationId) return undefined;
    // Mirrors the v3 orbital: latent elements reanalyse as 'latent', everything
    // else (incl. the Brand Equity top node) as 'measure'.
    const isLatent = response.latentElementScores?.some(
      (l) => l.element === drawerNode.canonical,
    );
    try {
      const result = await reanalyzeElement({
        jwtToken,
        organisationId,
        elementType: isLatent ? 'latent' : 'measure',
        elementName: drawerNode.canonical,
        brandEquityData: response,
      }).unwrap();
      return result.aiSummary;
    } catch {
      return undefined;
    }
  }, [drawerNode, response, jwtToken, organisationId, reanalyzeElement]);

  const items = useMemo(
    () =>
      data
        ? [
            {
              key: 'overview' as SubTab,
              label: (
                <span>
                  <FileTextOutlined style={{ marginRight: 8 }} />
                  <FormattedMessage id="marketerView.subtab.overview" defaultMessage="Overview" />
                </span>
              ),
              children: (
                <OverviewTab
                  data={data}
                  narrative={narrative}
                  narrativeLoading={narrativeLoading}
                  aiLoading={aiLoading}
                  response={response}
                  benchmarkMetrics={benchmarkMetrics}
                  onOpen={handleOpen}
                  sevenName={sevenName}
                  briefingLabel={briefingLabel}
                />
              ),
            },
            {
              key: 'areas' as SubTab,
              label: (
                <span>
                  <AppstoreOutlined style={{ marginRight: 8 }} />
                  <FormattedMessage id="marketerView.subtab.areas" defaultMessage="Areas" />
                </span>
              ),
              children: <ExploreTab data={data} onOpen={handleOpen} />,
            },
            {
              key: 'recommendations' as SubTab,
              label: (
                <span>
                  <CheckSquareOutlined style={{ marginRight: 8 }} />
                  <FormattedMessage
                    id="marketerView.subtab.recommendations"
                    defaultMessage="Recommendations"
                  />
                </span>
              ),
              children: (
                <RecommendationsTab
                  data={data}
                  narrative={narrative}
                  dismissedIds={dismissedRecommendationIds}
                  refetchNarrative={refetchNarrative}
                />
              ),
            },
          ]
        : [],
    [data, narrative, dismissedRecommendationIds, refetchNarrative, response, benchmarkMetrics, briefingLabel, handleOpen, sevenName],
  );

  // Show the spinner until data arrives (or a genuine error). Without the
  // `!isError` guard, the initial mount (isLoading still false, no data yet)
  // briefly hit the error Alert below — a flash when returning to the Briefing.
  // Below the response minimum a BE score can't be computed — gate the WHOLE
  // Briefing (no Overview/Areas/Recommendations, no AI) and nudge data
  // collection. Checked before the spinner so a never-building `data` (thin
  // data) doesn't leave the page spinning forever.
  if (response?.insufficientSampleSize) {
    // Keep the sub-tab bar so the user can see the depth of the Briefing that
    // unlocks with responses (mirrors Benchmarks keeping its tabs); the hero
    // (Finish setup / collect responses) is the shared content under each tab.
    const hero = (
      <MarketerInsufficientData totalResponses={response.totalResponses ?? 0} />
    );
    // SYR-90 — the pre-data state is exactly when The Hypothesis matters most:
    // it IS the day-0 content, and its progress bar motivates the collection
    // the hero asks for. Overview sub-tab only.
    const overviewHero = (
      <>
        <HypothesisCard />
        {hero}
      </>
    );
    return (
      <>
        {/* Recallable getting-started guide — only before any responses arrive
            (once ≥1 response is in, they've started). Opens the onboarding
            step-3 view read-only in a modal. Sits above the launch banner. */}
        {(response.totalResponses ?? 0) === 0 && hasActiveSurveys && (
        <>
        <div
          role="button"
          onClick={() => setGuideOpen(true)}
          style={{
            cursor: 'pointer',
            background: THEME.primarySoft,
            border: `1px solid ${THEME.primary}`,
            borderRadius: 14,
            padding: '14px 20px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700, color: THEME.textBase }}>
            <CompassOutlined style={{ color: THEME.primary, fontSize: 18 }} />
            {intl.formatMessage({ id: 'onboarding.survey.finalStep.roadmapTitle' })}
          </span>
          <span style={{ color: THEME.primary, fontWeight: 600, whiteSpace: 'nowrap' }}>
            <FormattedMessage id="marketerView.gettingStarted.view" defaultMessage="View the guide" />{' '}
            <RightOutlined />
          </span>
        </div>
        <Modal
          open={guideOpen}
          onCancel={() => setGuideOpen(false)}
          footer={null}
          width={1140}
          destroyOnClose
          styles={{ body: { paddingTop: 8 } }}
        >
          <SimpleOnboardingWizardV2 forceStep={2} embedded onComplete={() => setGuideOpen(false)} />
        </Modal>
        </>
        )}
        {/* Nudge to launch any active surveys with no responses yet — same as
            the dashboard, shown regardless of the (insufficient) BE score. */}
        <ShareSurveysBanner />
        <Tabs
          activeKey={subTab}
          onChange={(k) => {
            setSearchParams(k === 'overview' ? {} : { tab: k }, { replace: true });
            window.scrollTo({ top: 0 });
          }}
        items={[
          {
            key: 'overview',
            label: (
              <span>
                <FileTextOutlined style={{ marginRight: 8 }} />
                <FormattedMessage id="marketerView.subtab.overview" defaultMessage="Overview" />
              </span>
            ),
            children: overviewHero,
          },
          {
            key: 'areas',
            label: (
              <span>
                <AppstoreOutlined style={{ marginRight: 8 }} />
                <FormattedMessage id="marketerView.subtab.areas" defaultMessage="Areas" />
              </span>
            ),
            children: hero,
          },
          {
            key: 'recommendations',
            label: (
              <span>
                <CheckSquareOutlined style={{ marginRight: 8 }} />
                <FormattedMessage
                  id="marketerView.subtab.recommendations"
                  defaultMessage="Recommendations"
                />
              </span>
            ),
            children: hero,
          },
        ]}
        />
      </>
    );
  }

  if (!data && !isError) {
    return <MarketerLoading />;
  }

  if (isError) {
    const message =
      (error as { data?: { message?: string } })?.data?.message ??
      intl.formatMessage({
        id: 'marketerView.error.loadFailed',
        defaultMessage: 'Could not load brand equity data.',
      });
    return <Alert type="error" message={message} />;
  }

  return (
    <>
      {/* Nudge to launch any active surveys that have no responses yet. */}
      <ShareSurveysBanner />
      <Tabs
        activeKey={subTab}
        onChange={(k) => {
          setSearchParams(k === 'overview' ? {} : { tab: k }, { replace: true });
          window.scrollTo({ top: 0 });
        }}
        items={items}
      />
      <Drawer
        node={drawerNode}
        onClose={handleClose}
        description={drawerDescription}
        firstThoughts={drawerFirstThoughts}
        firstThoughtsLoading={aiLoading && !drawerFirstThoughts}
        benchmark={drawerBenchmark}
        onBenchmarkClick={handleBenchmarkClick}
        onViewInViz={handleViewInViz}
        onAnalyseAgain={handleAnalyseAgain}
      />
    </>
  );
};
